import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import get from "lodash/get";
import { FormattedDate, FormattedMessage } from "react-intl";
import { connect } from "react-redux";

import { myBookingShape } from "app/utils/propTypes";
import RelativeLink from "app/pages/.shared/RelativeLink";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import MyBooking from "app/pages/Booking/MyBooking";
import Amount from "app/pages/.shared/Amount";
import AlertMessage from "app/pages/.shared/AlertMessage";

import "./MyBookingItem.scss";
import DotNotification from "app/pages/.shared/DotNotification/DotNotification";
import Separator from "app/pages/.shared/Separator";
import MyBookingsPaymentContainer from "app/pages/Account/MyBookings/MyBookingsPayment/MyBookingsPaymentContainer";

const IMG_SIZES = {
	small: { width: 235 },
	medium: { width: 978 },
	large: { height: 150, width: 235 },
	xlarge: { height: 150, width: 235 },
};

class MyBookingItem extends React.Component {
	constructor() {
		super();
		this.state = {
			showPaymentForm: true,
		};
		this.handlePaymentSuccess = this.handlePaymentSuccess.bind(this);
	}

	handlePaymentSuccess() {
		this.setState({
			showPaymentForm: false,
		});

		if (typeof this.props.onPaymentSuccess === "function") {
			this.props.onPaymentSuccess();
		}
	}

	render() {
		const { myBooking, resolution, enablePaymentForm } = this.props;

		const {
			name,
			category,
			country,
			region,
			resort,
			photo = {},
			duration,
			departureDate,
			endDate,
			children,
			infants,
			adults,
			offer,
			customerRequestSummary = {},
		} = myBooking;

		const ficheProduit = { name, category, country, region, resort };
		ficheProduit.photos = [photo];

		const booking = {
			duration: { value: duration },
			departureDate,
			endDate,
			children,
			infants,
			adults,
			offer,
			flight: {
				inbound: {
					legs: [
						{
							from: {
								takeOffDate: endDate, // pour le cas ou offer.type === "FA", la date de retour est la date de decollage
							},
						},
					],
				},
			},
		};

		const isUnpaidBooking = myBooking.totalDue > 0;
		const isCanceledBooking = myBooking.status === "CANCELED";
		const confirmationUrl = get(myBooking, "bookingDocument.confirmation");
		const invoiceUrl = get(myBooking, "bookingDocument.invoice");
		const voucherUrl = get(myBooking, "bookingDocument.voucher");
		const hasAllDocuments = confirmationUrl && invoiceUrl && voucherUrl;

		const checkinUrl = get(myBooking, "urls.faqCheckinUrl");

		const processingStatusMessage = isUnpaidBooking ? (
			<FormattedMessage id="my.bookings.my.documents.invoice.waiting.full.payment" />
		) : (
			<FormattedMessage id="my.bookings.my.documents.invoice.processing" />
		);

		return (
			<div className="my-booking-item">
				<div className="my-booking-item__help">
					<RelativeLink
						className="my-booking-item__help__button"
						data-cy="new-request-button"
						to={{
							pathname: `/account/customer-request/${myBooking.bookingReference}/new`,
						}}
					>
						<i className="icon icon--email-white" />
						<FormattedMessage id="my.bookings.new.request.button.label" />
					</RelativeLink>

					{(customerRequestSummary.requestOpen > 0 ||
						customerRequestSummary.requestClosed > 0) && (
						<RelativeLink
							className="my-booking-item__help__button my-booking-item__help__button--reverse"
							to={{
								pathname: `/account/customer-request/${myBooking.bookingReference}`,
							}}
						>
							<i className="icon icon--suivi-white" />
							<FormattedMessage
								id="my.bookings.current.requests.button.label"
								tagName="div"
							/>
							{customerRequestSummary.requestNotRead > 0 && (
								<DotNotification text={customerRequestSummary.requestNotRead} />
							)}
						</RelativeLink>
					)}
				</div>

				<div className="my-booking-item__status">
					<div className="my-booking-item__reference">
						<FormattedMessage
							values={{
								reference: myBooking.bookingReference,
							}}
							id="my.bookings.reference.label"
						/>
						{" : "}
						<div
							className={`my-booking-item__status-label my-booking-item__reference--${myBooking.status &&
								myBooking.status.toLowerCase()}`}
						>
							<FormattedMessage
								id={`my.bookings.booking.status.${myBooking.status &&
									myBooking.status.toLowerCase()}`}
							/>
						</div>
					</div>
					<div className="my-booking-item__date">
						<FormattedDate
							timeZone="UTC"
							value={new Date(myBooking.bookingDate)}
							day="2-digit"
							month="2-digit"
							year="numeric"
						/>
					</div>
				</div>

				<div className="my-booking-item__content">
					<div className="my-booking-item__booking">
						<div className="my-booking-item__title">
							<FormattedMessage id="my.bookings.my.booking.label" />
						</div>
						<div
							className={classNames("my-booking-item__booking-recap", {
								"my-booking-item__booking-recap--canceled":
									myBooking.status === "CANCELED",
							})}
						>
							<div className="my-booking-item__picture">
								<ProgressiveImg
									sizes={IMG_SIZES}
									src={photo.url}
									thumbnail={photo.thumbnail}
								/>
							</div>
							<div
								className={classNames("my-booking-item__recap", {
									"my-booking-item__recap--canceled":
										myBooking.status === "CANCELED",
								})}
							>
								<MyBooking
									ficheProduit={ficheProduit}
									booking={booking}
									resolution={resolution}
								/>
							</div>
						</div>
					</div>

					<div className="my-booking-item__documents">
						<div className="my-booking-item__title">
							<FormattedMessage id="my.bookings.my.documents.label" />
						</div>
						<div className="my-booking-item__action">
							<div className="my-booking-item__document-container">
								<div
									className={classNames({
										"my-booking-item__document-label": true,
										"my-booking-item__document-label--active": confirmationUrl,
									})}
								>
									<div className="my-booking-item__document">
										<div className="my-booking-item__document-label-primary my-booking-item__document-label-primary--first">
											<FormattedMessage id="my.bookings.confirmation.document.button.label" />
										</div>
										<div className="my-booking-item__document-label-secondary my-booking-item__document-label-secondary--first">
											{confirmationUrl ? (
												<FormattedMessage id="my.bookings.my.documents.confirmation.received" />
											) : (
												<FormattedMessage id="my.bookings.my.documents.confirmation.processing" />
											)}
										</div>
									</div>
								</div>
								{confirmationUrl && (
									<RelativeLink
										data-cy="mybookings-confirmation"
										rel="noopener noreferrer"
										target="_blank"
										to={{ pathname: confirmationUrl }}
									>
										<FormattedMessage id="my.bookings.my.documents.link.label" />
									</RelativeLink>
								)}
							</div>
							<div className="my-booking-item__document-container">
								<div
									className={classNames({
										"my-booking-item__document-label": true,
										"my-booking-item__document-label--active": invoiceUrl,
									})}
								>
									<div className="my-booking-item__document">
										<div className="my-booking-item__document-label-primary">
											<FormattedMessage id="my.bookings.invoice.button.label" />
										</div>

										<div className="my-booking-item__document-label-secondary">
											{invoiceUrl ? (
												<FormattedMessage id="my.bookings.my.documents.confirmation.received" />
											) : (
												processingStatusMessage
											)}
										</div>
									</div>
								</div>
								{invoiceUrl && (
									<RelativeLink
										data-cy="mybookings-invoice"
										rel="noopener noreferrer"
										target="_blank"
										to={{ pathname: invoiceUrl }}
									>
										<FormattedMessage id="my.bookings.my.documents.link.label" />
									</RelativeLink>
								)}
							</div>
							<div className="my-booking-item__document-container">
								<div
									className={classNames({
										"my-booking-item__document-label": true,
										"my-booking-item__document-label--active": voucherUrl,
									})}
								>
									<div className="my-booking-item__document">
										<div className="my-booking-item__document-label-primary">
											<FormattedMessage id="my.bookings.travel.document.button.label" />
										</div>

										<div className="my-booking-item__document-label-secondary">
											{voucherUrl ? (
												<FormattedMessage id="my.bookings.my.documents.confirmation.received" />
											) : (
												processingStatusMessage
											)}
										</div>
									</div>
								</div>
								{voucherUrl && (
									<RelativeLink
										data-cy="mybookings-voucher"
										rel="noopener noreferrer"
										target="_blank"
										to={{
											pathname: voucherUrl,
										}}
									>
										<FormattedMessage id="my.bookings.my.documents.link.label" />
									</RelativeLink>
								)}
							</div>
							{hasAllDocuments && (
								<div className="my-booking-item__documents-note">
									<FormattedMessage id="my.bookings.my.documents.confirmation.note" />
								</div>
							)}
						</div>
					</div>

					{checkinUrl && hasAllDocuments && (
						<div className="my-booking-item__checkin">
							<div className="my-booking-item__title">
								<FormattedMessage id="my.bookings.my.booking.checkin.label" />
							</div>
							<RelativeLink
								data-cy="mybookings-checkin-button"
								design="outlined"
								rel="noopener noreferrer"
								target="_blank"
								to={{
									pathname: checkinUrl,
								}}
							>
								<FormattedMessage id="my.bookings.my.booking.checkin.button.label" />
							</RelativeLink>
						</div>
					)}

					<div className="my-booking-item__solde">
						<div className="my-booking-item__solde-container">
							<div className="my-booking-item__title">
								<FormattedMessage id="my.bookings.my.solde.label" />
							</div>
							<div className="solde">
								<div className="solde__item">
									<div className="solde__label">
										<FormattedMessage id="my.bookings.total.label" />
									</div>
									<div className="solde__amount">
										{/* Utiliser Amount au lieu de AmountContainer car la devise est celui du booking au non celui du partnercode */}
										<Amount
											amount={myBooking.total}
											currencyCode={myBooking.currencyCode}
										/>
									</div>
								</div>

								<div className="solde__item">
									<div className="solde__label">
										<FormattedMessage id="my.bookings.paid.label" />
									</div>
									<div className="solde__amount">
										{/* Utiliser Amount au lieu de AmountContainer car la devise est celui du booking au non celui du partnercode */}
										<Amount
											amount={myBooking.totalPaid}
											currencyCode={myBooking.currencyCode}
										/>
									</div>
								</div>
								{isUnpaidBooking && (
									<div
										className={classNames({
											solde__item: true,
											"solde__item--warning": isUnpaidBooking,
										})}
									>
										<div className="solde__label">
											<FormattedMessage
												id="my.bookings.remained.label"
												values={{
													date: (
														<FormattedDate value={myBooking.dueDate} />
													),
												}}
											/>
										</div>
										<div className="solde__amount">
											{/* Utiliser Amount au lieu de AmountContainer car la devise est celui du booking au non celui du partnercode */}
											<Amount
												amount={myBooking.totalDue}
												currencyCode={myBooking.currencyCode}
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					{isUnpaidBooking && (
						<React.Fragment>
							{enablePaymentForm ? (
								<div
									className="my-booking-item__payment"
									data-cy="mybookings-payment-form"
								>
									<Separator design="middle" />

									{!this.state.showPaymentForm ? (
										<div className="my-booking-item__message my-booking-item__message--success">
											<FormattedMessage id="my.bookings.pay.success.button.label" />
										</div>
									) : (
										<MyBookingsPaymentContainer
											onPaymentSuccess={this.handlePaymentSuccess}
											key={myBooking.bookingReference}
											brand={myBooking.brand}
											email={myBooking.email}
											shop={myBooking.shop}
											bookingReference={myBooking.bookingReference}
											amount={myBooking.totalDue}
										/>
									)}
								</div>
							) : (
								<div className="my-booking-item__help">
									<RelativeLink
										className="my-booking-item__help__button"
										data-cy="go-to-payment-form-button"
										to={{
											pathname: `/account/mybookings/${
												myBooking.bookingReference
											}`,
										}}
									>
										<FormattedMessage id="my.bookings.show.payment.form.button.label" />
									</RelativeLink>
								</div>
							)}
						</React.Fragment>
					)}
				</div>
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		resolution: state.resolution,
	};
};

MyBookingItem.propTypes = {
	myBooking: PropTypes.shape(myBookingShape),
	resolution: PropTypes.string,
	enablePaymentForm: PropTypes.bool,
	onPaymentSuccess: PropTypes.func,
};

MyBookingItem.defaultProps = {
	photo: {},
};

export default connect(mapStateToProps)(MyBookingItem);
